import { HStack, Separator, Text } from '@chakra-ui/react'
import React from 'react'
import { FaLockOpen } from 'react-icons/fa'

import type { NavDoor } from '@/components/navigation/nav.types'
import { useUserMenuRail } from '@/components/navigation/useContextRail'
import {
  MenuContent,
  MenuItem,
  MenuRoot,
  MenuTriggerItem,
} from '@/components/ui/menu'
import { UserRoutes } from '@/routes/_hooks/routes.enum'
import { Link } from '@/routing'

import type { UserMenuContentProps } from '../UserMenuContent.types'

type UserMenuDropdownPrimaryNavProps = Pick<
  UserMenuContentProps,
  'user' | 'captureEvent' | 't' | 'endImpersonation' | 'isImpersonating'
> & {
  rowPx: number
  rowPy: number
  rowGap: number
}

/**
 * The user menu's destinations: the personal space's, always the same.
 *
 * It used to be a hand-written echo — one "door" for the active context, then Mon matériel,
 * Mes réservations, Mes alertes, Mes communautés, Mes réseaux, assembled from
 * `useContextualPath` with its own idea of which context was active. Two lists to keep in step,
 * and the menu was always the one a release behind: it never learned about Mes listes, and it
 * never offered Réglages at all, so the settings of a network could only be reached from the
 * rail.
 *
 * It then went too far the other way and became the rail *exactly*, which made it change shape
 * with every navigation: the rows moved under the cursor as one crossed from a network to a
 * community. A menu in a fixed position should offer a fixed list, so this reads
 * {@link useUserMenuRail} — the personal space's rows, wherever one is standing.
 *
 * The one thing that legitimately follows the context is Réglages, because settings are always
 * the settings of *something*. It carries what lies behind it as a submenu.
 *
 * Rendered as `MenuItem`s rather than by reusing `ContextRail` itself: inside a Chakra menu the
 * keyboard and screen-reader behaviour belongs to the menu, and a rail of plain links dropped
 * in the middle of it would break arrow-key navigation.
 */
export const UserMenuDropdownPrimaryNav = ({
  rowPx,
  rowPy,
  rowGap,
  user,
  captureEvent,
  t,
  endImpersonation,
  isImpersonating,
}: UserMenuDropdownPrimaryNavProps) => {
  const rail = useUserMenuRail()

  return (
    <>
      {rail.sections.map((section, index) => (
        <React.Fragment key={section.key}>
          {index > 0 ? <Separator my={1} /> : null}
          {section.label ? (
            <Text
              px={rowPx}
              pt={2}
              pb={1}
              fontSize="10px"
              fontWeight="bold"
              letterSpacing="0.08em"
              textTransform="uppercase"
              color="fg.subtle"
            >
              {section.label}
            </Text>
          ) : null}
          {section.entries.map((entry) => {
            const Icon = entry.icon

            return (
              <MenuItem
                key={entry.key}
                px={rowPx}
                py={rowPy}
                cursor={entry.disabled ? 'default' : 'pointer'}
                disabled={entry.disabled}
                value={entry.to}
                asChild={!entry.disabled}
              >
                {entry.disabled ? (
                  <HStack gap={rowGap} color="fg.subtle">
                    {Icon ? <Icon /> : null}
                    <span>{entry.label}</span>
                    {entry.badge}
                  </HStack>
                ) : (
                  <Link to={entry.to}>
                    <HStack gap={rowGap}>
                      {Icon ? <Icon /> : null}
                      <span>{entry.label}</span>
                    </HStack>
                  </Link>
                )}
              </MenuItem>
            )
          })}
        </React.Fragment>
      ))}

      {/* The door out, kept last and set apart — the same place it holds in the rail. */}
      {rail.door ? (
        <>
          <Separator my={1} />
          <SettingsDoor
            door={rail.door}
            rowPx={rowPx}
            rowPy={rowPy}
            rowGap={rowGap}
          />
        </>
      ) : null}

      <Separator my={1} />

      {isImpersonating ? (
        <MenuItem
          px={rowPx}
          py={rowPy}
          cursor="pointer"
          value="end-impersonation"
          onClick={() => {
            endImpersonation()
            captureEvent('navigation_end_impersonation_clicked', {
              user_id: user?.id,
            })
          }}
        >
          {t('admin:accountManagement.users.actions.endImpersonation.label')}
        </MenuItem>
      ) : (
        <MenuItem
          px={rowPx}
          py={rowPy}
          cursor="pointer"
          value={UserRoutes.Logout}
          asChild
        >
          <Link
            to={UserRoutes.Logout}
            onClick={() =>
              captureEvent('navigation_logout_clicked', {
                destination: UserRoutes.Logout,
                user_id: user?.id,
              })
            }
          >
            <HStack gap={rowGap}>
              <FaLockOpen />
              <span>{t('menu.logout')}</span>
            </HStack>
          </Link>
        </MenuItem>
      )}
    </>
  )
}

/**
 * Réglages: a row that opens onto the current object's settings screens.
 *
 * It used to be a plain link wearing a chevron, which is a promise a link cannot keep — the arrow
 * said "Général, SSO, Webhooks, Marque blanche, Abonnement are one hover away" and the row simply
 * navigated. `MenuTriggerItem` draws its own chevron and means it.
 *
 * The trigger still goes somewhere when clicked rather than only opening: someone who wants the
 * settings and not a particular screen should not have to aim at a submenu entry.
 *
 * Falls back to the plain link when there is nothing behind the door, so the chevron and the
 * submenu appear or disappear together and the row never over-promises again.
 */
const SettingsDoor = ({
  door,
  rowPx,
  rowPy,
  rowGap,
}: {
  door: NavDoor
  rowPx: number
  rowPy: number
  rowGap: number
}) => {
  const entries = door.behind?.entries ?? []

  if (entries.length === 0) {
    return (
      <MenuItem px={rowPx} py={rowPy} cursor="pointer" value={door.to} asChild>
        <Link to={door.to}>
          <HStack gap={rowGap} w="full">
            {door.icon ? <door.icon /> : null}
            <span>{door.label}</span>
          </HStack>
        </Link>
      </MenuItem>
    )
  }

  return (
    <MenuRoot positioning={{ placement: 'right-start', gutter: 2 }}>
      <MenuTriggerItem px={rowPx} py={rowPy} cursor="pointer" value={door.key}>
        <HStack gap={rowGap} w="full">
          {door.icon ? <door.icon /> : null}
          <span>{door.label}</span>
        </HStack>
      </MenuTriggerItem>
      <MenuContent>
        {door.behind?.label ? (
          <Text
            px={rowPx}
            pt={2}
            pb={1}
            fontSize="10px"
            fontWeight="bold"
            letterSpacing="0.1em"
            textTransform="uppercase"
            color="fg.subtle"
            truncate
          >
            {door.behind.label}
          </Text>
        ) : null}

        {entries.map((entry) => (
          <MenuItem
            key={entry.key}
            px={rowPx}
            py={rowPy}
            cursor={entry.disabled ? 'default' : 'pointer'}
            value={entry.to}
            disabled={entry.disabled}
            asChild={!entry.disabled}
          >
            {entry.disabled ? (
              <HStack gap={rowGap} w="full" opacity={0.6}>
                {entry.icon ? <entry.icon /> : null}
                <span>{entry.label}</span>
                {entry.badge ? <HStack ml="auto">{entry.badge}</HStack> : null}
              </HStack>
            ) : (
              <Link to={entry.to}>
                <HStack gap={rowGap} w="full">
                  {entry.icon ? <entry.icon /> : null}
                  <span>{entry.label}</span>
                  {entry.badge ? (
                    <HStack ml="auto">{entry.badge}</HStack>
                  ) : null}
                </HStack>
              </Link>
            )}
          </MenuItem>
        ))}
      </MenuContent>
    </MenuRoot>
  )
}
